import React, { useEffect, useState, useCallback } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useTheme } from '@techstyle/react-components';
import { Countdown, Mobile } from '@techstyle/react-components-legacy';
import { FormattedMessage } from '@techstyle/react-intl';

import { Container as AssetCountdown } from '../AssetCountdown';
import { useCountdownTimer } from '../CountdownTimerProvider/CountdownTimerProvider';

const CountdownTimerWrapper = styled.div`
  display: flex;
  padding: 7px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  background: ${props => props.theme.colors.lavender250};
  color: ${props => props.theme.colors.default};
`;

const CountdownMessage = styled.div`
  font-size: 10px;
  line-height: 18px;
  letter-spacing: 1.75px;
  text-transform: uppercase;
`;

const StyledCountdown = styled(AssetCountdown)`
  display: inline-block;
  width: auto;
  margin-bottom: 2px;
  padding: 0 0 0 ${props => props.theme.spacing.tiny}px;

  & ${AssetCountdown.StyledCountdown} {
    font-size: 6px;
    margin-bottom: 0;
  }

  & ${Countdown.CountdownNumber} {
    font-size: 18px;
    font-weight: 500;
  }

  & ${Countdown.CountdownLabel} {
    display: none;
  }
`;

const CountdownTimerHeader = ({
  className = '',
  renderDefaultTimer,
  onCountdownEnd,
  countdownLabel = '',
  isBuilderPreview = false,
  builderPreviewTime,
}) => {
  const {
    endDate,
    isCountdownActive,
    setIsCountdownActive,
    setIsHeaderVisible,
  } = useCountdownTimer();

  useEffect(() => {
    setIsHeaderVisible(true);

    return () => setIsHeaderVisible(false);
  }, [setIsHeaderVisible]);

  const { nav } = useTheme();
  const [isMobile, setMobile] = useState(false);

  const navbarHeight = !isMobile
    ? nav.desktopHeight
    : nav.mobileHeight + nav.mobileSubNav;

  const handleCountdownComplete = () => {
    setIsCountdownActive(false);
    if (typeof onCountdownEnd === 'function') {
      onCountdownEnd();
    }
  };

  const handleIntersection = useCallback(
    e => setIsHeaderVisible(e.isIntersecting),
    [setIsHeaderVisible]
  );

  const handleMobile = useCallback(
    isMobileStatus => {
      setMobile(isMobileStatus);
    },
    [setMobile]
  );

  if (!isCountdownActive && renderDefaultTimer) {
    return renderDefaultTimer();
  }

  // if we are not live in Builder show it
  if (!isCountdownActive && !isBuilderPreview) {
    return null;
  }

  return (
    <Observer
      onChange={handleIntersection}
      rootMargin={`-${navbarHeight}px 0px 0px 0px`}
    >
      <CountdownTimerWrapper className={className}>
        <CountdownMessage>
          {countdownLabel || (
            <FormattedMessage
              defaultMessage="Offer Ends In"
              id="global_checkout.offer_ends_in"
            />
          )}
        </CountdownMessage>
        <StyledCountdown
          countDownEndDate={isBuilderPreview ? builderPreviewTime : endDate}
          onCountdownComplete={handleCountdownComplete}
        />
        <Mobile onChange={handleMobile} />
      </CountdownTimerWrapper>
    </Observer>
  );
};

CountdownTimerHeader.CountdownTimerWrapper = CountdownTimerWrapper;
CountdownTimerHeader.CountdownMessage = CountdownMessage;
CountdownTimerHeader.StyledCountdown = StyledCountdown;

CountdownTimerHeader.propTypes = {
  className: PropTypes.string,
  renderDefaultTimer: PropTypes.func,
  onCountdownEnd: PropTypes.func,
  countdownLabel: PropTypes.string,
};

export default CountdownTimerHeader;
